/**
 * FlahaSOIL v2 — Base saturation chart adapter (Phase 10C-D).
 */
import type { SoilChemistryResultDTO } from "@flaha/shared-types";
import type { ScientificChartData, ScientificChartStatus } from "../types/ScientificChartTypes";
import { CHART_UNITS } from "../utils/chartUnits";

export interface BaseSaturationChartData {
  caPercent: number;
  mgPercent: number;
  kPercent: number;
  naPercent: number;
  otherPercent: number;
  baseSaturation: number;
  esp: number;
  cec: number;
}

export function baseSaturationChartAdapter(
  chemistry: SoilChemistryResultDTO | null
): ScientificChartData<BaseSaturationChartData | null> {
  let status: ScientificChartStatus = "READY";

  if (!chemistry) {
    status = "MISSING_DATA";
    return {
      status,
      title: "CEC / Base Saturation",
      description: "Exchange complex composition and base saturation levels.",
      data: null,
      warnings: [],
      units: { percent: CHART_UNITS.percent, cec: CHART_UNITS.cec },
      source: "Soil Analysis Result",
      accessibilitySummary: "No data available for base saturation chart.",
    };
  }

  const data: BaseSaturationChartData = {
    caPercent: chemistry.caPercent,
    mgPercent: chemistry.mgPercent,
    kPercent: chemistry.kPercent,
    naPercent: chemistry.naPercent,
    otherPercent: chemistry.cationBalanceOther,
    baseSaturation: chemistry.baseSaturation,
    esp: chemistry.esp,
    cec: chemistry.cec,
  };

  return {
    status: "READY",
    title: "CEC / Base Saturation",
    description: "Exchange complex composition and base saturation levels.",
    data,
    warnings: [],
    units: { percent: CHART_UNITS.percent, cec: CHART_UNITS.cec },
    source: "Soil Analysis Result",
    accessibilitySummary: `Base saturation is ${data.baseSaturation.toFixed(1)}%. ESP is ${data.esp.toFixed(1)}%. CEC is ${data.cec.toFixed(1)} ${CHART_UNITS.cec}.`,
  };
}
